"use client";

import { sendContactForm } from "@entities/contact/api/actions";
import { contactFormSchema } from "@entities/contact/model";
import { ContactForm } from "@entities/contact/types";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecaptcha } from "@shared/lib/recaptcha";
import { Button } from "@shared/ui/button";
import { Spinner } from "@shared/ui/spinner";
import { CountryCodeSelector } from "@widgets/country-code-selector";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { Controller, useForm } from "react-hook-form";

export function Form() {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [hasApiError, setApiError] = useState(false);
  const { register, control, handleSubmit, formState } = useForm<ContactForm>({
    resolver: yupResolver(contactFormSchema),
    defaultValues: {
      ddd: "+55",
    },
  });

  const { getToken } = useRecaptcha();

  const onSubmit = (data: ContactForm) =>
    startTransition(async () => {
      setApiError(false);
      const token = await getToken();

      await sendContactForm(data, token)
        .then(() => router.push("/contact/thank-you"))
        .catch(() => setApiError(true));
    });

  return (
    <form
      className={
        "flex-1 flex flex-col gap-4 placeholder-gray-400 text-black-pearl-950"
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <div
        className={`flex flex-col gap-4 transition-all ${isPending && "opacity-70"}`}
      >
        <div className="flex flex-col gap-1">
          <input
            className={`p-4 rounded border ${
              formState.errors.name ? "border-red-500" : "border-gray-300"
            } outline outline-0 focus:outline-2 focus:outline-antique-brass-500`}
            placeholder={"Nome:"}
            {...register("name")}
            required={true}
          />
          {formState.errors.name && (
            <span className="text-red-500 text-sm">
              {formState.errors.name.message}
            </span>
          )}
        </div>

        <div className={"flex flex-col gap-1"}>
          <div className={"flex flex-row gap-4"}>
            <Controller
              name={"ddd"}
              control={control}
              render={({ field }) => (
                <CountryCodeSelector
                  value={field.value}
                  onChange={field.onChange}
                  required={true}
                />
              )}
            />

            <input
              className={`w-full p-4 rounded border ${
                formState.errors.phone ? "border-red-500" : "border-gray-300"
              } outline outline-0 focus:outline-2 focus:outline-antique-brass-500`}
              placeholder={"Telefone:"}
              {...register("phone")}
              required={true}
            />
          </div>
          {formState.errors.phone && (
            <span className="text-red-500 text-sm">
              {formState.errors.phone.message}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <input
            className={`p-4 rounded border ${
              formState.errors.mail_address
                ? "border-red-500"
                : "border-gray-300"
            } outline outline-0 focus:outline-2 focus:outline-antique-brass-500`}
            placeholder={"E-mail:"}
            {...register("mail_address")}
            required={true}
            type={"email"}
          />
          {formState.errors.mail_address && (
            <span className="text-red-500 text-sm">
              {formState.errors.mail_address.message}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <textarea
            className={`p-4 rounded border ${
              formState.errors.content ? "border-red-500" : "border-gray-300"
            } outline outline-0 focus:outline-2 focus:outline-antique-brass-500`}
            rows={4}
            placeholder={"Mensagem:"}
            {...register("content")}
            required={true}
          />
          {formState.errors.content && (
            <span className="text-red-500 text-sm">
              {formState.errors.content.message}
            </span>
          )}
        </div>
      </div>

      {hasApiError && (
        <div className="text-red-500">
          Ocorreu um erro ao enviar o formulário. Tente novamente.
        </div>
      )}

      <small className={"overflow-hidden sm:text-nowrap sm:whitespace-nowrap"}>
        Protegido por reCAPTCHA,{" "}
        <a
          className={"text-black-pearl-800"}
          href={"https://policies.google.com/privacy"}
          target={"_blank"}
        >
          Política de Privacidade
        </a>{" "}
        &{" "}
        <a
          className={"text-black-pearl-800"}
          href={"https://policies.google.com/terms"}
          target={"_blank"}
        >
          Termos de Serviço
        </a>{" "}
        aplicam.
      </small>

      <div className={"flex gap-4"}>
        <Button
          type={"submit"}
          title={"Enviar"}
          className={"lg:w-fit !text-2xl"}
          disabled={isPending}
        />

        {isPending && (
          <Spinner size={"sm"} className={"!w-fit !max-w-fit !h-full"} />
        )}
      </div>
    </form>
  );
}
